import { useId, useRef, useState } from 'react';
import { Check, MessageSquare } from 'lucide-react';

export interface FeedbackInput {
  requestId: string;
  kind: 'problem' | 'idea';
  message: string;
  roomCode?: string;
  source: 'web' | 'ios' | 'android';
}

export async function submitFeedback(input: FeedbackInput): Promise<void> {
  const response = await fetch('/api/feedback', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input), signal: AbortSignal.timeout(15000),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Could not send your feedback. Please try again.');
  }
}

interface FeedbackFormProps {
  roomCode?: string;
  source?: FeedbackInput['source'];
  onSubmit?: (input: FeedbackInput) => Promise<void>;
  onNew?: () => void;
}

export default function FeedbackForm({ roomCode, source = 'web', onSubmit = submitFeedback, onNew }: FeedbackFormProps) {
  const id = useId();
  const [kind, setKind] = useState<FeedbackInput['kind']>('problem');
  const [message, setMessage] = useState('');
  const [includeRoom, setIncludeRoom] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const attempt = useRef<{ fingerprint: string; requestId: string } | null>(null);
  const inFlight = useRef(false);

  async function send(event: React.FormEvent) {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setSending(true);
    setError('');
    try {
      const input = { kind, message: message.trim(), source, ...(includeRoom && roomCode ? { roomCode } : {}) };
      const fingerprint = JSON.stringify(input);
      if (attempt.current?.fingerprint !== fingerprint) attempt.current = { fingerprint, requestId: crypto.randomUUID() };
      await onSubmit({ ...input, requestId: attempt.current.requestId });
      setSent(true);
    } catch (failure) {
      setError(failure instanceof Error && !['TimeoutError', 'TypeError'].includes(failure.name)
        ? failure.message : 'Could not reach Evoke. Your message is still here. Check your connection and try again.');
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  if (sent) return <div className="ev-feedback-success" role="status"><Check size={32} aria-hidden="true" /><h2 className="ev-display">Thanks for helping Evoke grow.</h2><p>Your feedback is in our private inbox. We’ll read it as we improve the game.</p><button type="button" className="ev-button ev-button-secondary mt-5" onClick={() => { setSent(false); setMessage(''); setError(''); setIncludeRoom(true); attempt.current = null; onNew?.(); }}>Write another note</button></div>;

  return <form onSubmit={send} className="ev-feedback-form">
    <p>Something go wrong, or have an idea for your next game? We’d love to hear it.</p>
    <fieldset disabled={sending}>
      <legend className="ev-label">What would you like to share?</legend>
      <div className="ev-feedback-kinds">{([['problem', 'Report a problem'], ['idea', 'Share an idea']] as const).map(([value, label]) => <label key={value}><input type="radio" name={`${id}-kind`} checked={kind === value} onChange={() => setKind(value)} />{label}</label>)}</div>
      <label className="ev-label" htmlFor={`${id}-message`}>Your message</label>
      <textarea id={`${id}-message`} className="ev-input" value={message} onChange={event => setMessage(event.target.value)} minLength={10} maxLength={2000} required rows={5} aria-describedby={`${id}-hint`} placeholder={kind === 'problem' ? 'What happened, and what were you expecting? Which device or browser were you using?' : 'What would make game night better?'} />
      <p id={`${id}-hint`} className="ev-feedback-hint">10–2,000 characters · {message.length.toLocaleString()}/2,000</p>
      {roomCode && <label className="ev-feedback-context"><input type="checkbox" checked={includeRoom} onChange={event => setIncludeRoom(event.target.checked)} /> Include room code {roomCode}</label>}
      <p className="ev-feedback-hint">Only the Evoke team can read this. We send your message, feedback type, platform{roomCode ? ', and room code if selected' : ''}. Please leave out passwords and personal details. This form doesn’t collect contact information, so we can’t reply directly.</p>
      <button className="ev-button" type="submit" disabled={sending || message.trim().length < 10}><MessageSquare size={16} aria-hidden="true" />{sending ? 'Sending…' : 'Send feedback'}</button>
    </fieldset>
    {error && <p role="alert" className="ev-alert">{error}</p>}
  </form>;
}
